"use client";
import React, { useState } from "react";
import {
  Box, Button, Typography, Stack, Switch, Collapse, 
  IconButton, Paper, CircularProgress, Accordion, 
  AccordionSummary, AccordionDetails 
} from "@mui/material";
import {
  AddCircleRounded as AddCircleRoundedIcon,
  Print as PrintIcon,
  ExpandMore,
  HistoryEduOutlined
} from "@mui/icons-material";
import { useParams } from "react-router-dom";
import { useQuery } from "react-query";
import useClientDetails from "../../../../../hooks/useClientDetails";
import { getMentalCapacityAssessments } from "../../../../../api/services/Assessment/ConsentCapacity";
import AssessmentTable from "../ActiveCareTabs/ConsentCapacity/ConsentTable";
import { QK } from "../../../../../base/qk";
import ConsentDialog from "../ActiveCareTabs/ConsentCapacity/ConsentDialog";

const ConsentTabs = () => {
  const [showData, setShowData] = useState(false);
  const [showClosed, setShowClosed] = useState(false);
  
  // Dialog States
  const [openDialog, setOpenDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const { id: clientID } = useParams();
  const { fullName } = useClientDetails(clientID);

  const { data, isLoading } = useQuery(
    [QK.consentAssessments, clientID],
    () => getMentalCapacityAssessments(clientID),
    { enabled: !!clientID && showData }
  );

  const uniqueAssessments = data?.reduce((acc, current) => {
    const reasonId = current.consent_reason?.id || "general";
    if (!acc.find((item) => (item.consent_reason?.id || "general") === reasonId)) {
      acc.push(current);
    }
    return acc;
  }, []);

  const handleShowClick = () => setShowData(!showData);

  const handleEdit = (item) => {
    setEditingItem(item);
    setOpenDialog(true);
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Button
        fullWidth
        onClick={handleShowClick}
        variant="contained"
        sx={{
          justifyContent: "space-between", py: 1.8, px: 2.5, borderRadius: 2,
          fontWeight: 600, textTransform: "none", mb: 3,
          background: showData
            ? "linear-gradient(90deg, #0288d1, #03a9f4)"
            : "linear-gradient(90deg, #1976d2, #42a5f5)",
          color: "white", boxShadow: "0px 2px 6px rgba(0,0,0,0.1)",
          "&:hover": { background: "linear-gradient(90deg, #1565c0, #29b6f6)" },
        }}
      >
        Consent and Mental Capacity for {fullName}
      </Button>

      <Collapse in={showData}>
        <Paper
          elevation={2}
          sx={{ p: 2, borderRadius: 2, bgcolor: "#fafafa", mb: 2,
            display: "flex", justifyContent: "space-between", alignItems: "center" }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="body1" color="text.secondary">
              {fullName}&apos;s Consent and Mental Capacity List
            </Typography>
            <IconButton 
                size="small" 
                color="primary" 
                onClick={() => {
                    setEditingItem(null);
                    setOpenDialog(true);
                }}
            >
              <AddCircleRoundedIcon fontSize="small" />
            </IconButton>
          </Stack>

          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="body2" color="text.secondary">Show Closed Consent</Typography>
            <Switch checked={showClosed} onChange={(e) => setShowClosed(e.target.checked)} color="primary" />
          </Stack>
        </Paper>

        {isLoading ? (
          <Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress size={24} /></Box>
        ) : (
          <Stack spacing={1}>
            {uniqueAssessments?.length > 0 ? (
              uniqueAssessments.map((item) => (
                <Accordion 
                  key={item.id} 
                  elevation={0} 
                  sx={{ border: "1px solid #e2e8f0", borderRadius: "8px !important" }}
                >
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <HistoryEduOutlined sx={{ color: "#6366f1", fontSize: 20 }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        {item.consent_reason?.name || "General Consent"}
                      </Typography>
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 0, borderTop: "1px solid #f1f5f9" }}>
                    <Box sx={{ p: 1 }}>
                      <AssessmentTable
                        consentReasonId={item.consent_reason?.id} 
                        clientID={clientID}
                        onEdit={handleEdit} // Table এর ভেতর থেকে Edit বাটন চাপলে এটি ট্রিগার হবে
                      />
                    </Box>
                  </AccordionDetails>
                </Accordion>
              ))
            ) : (
              <Typography variant="body2" sx={{ textAlign: 'center', py: 2, color: '#94a3b8' }}>
                No records found.
              </Typography>
            )}
          </Stack>
        )}
      </Collapse>

      {/* Consent Dialog Component */}
      <ConsentDialog
        open={openDialog}
        handleClose={() => {
          setOpenDialog(false);
          setEditingItem(null);
        }}
        editingItem={editingItem}
      />
    </Box>
  );
};

export default ConsentTabs;